import React, { useState, useEffect, useLayoutEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  Menu,
  X,
  ChevronDown,
  Home,
  Users,
  Building,
  GraduationCap,
  Calendar,
  Image,
  Award,
  Phone,
  FileText,
  UserCheck,
  LogIn,
  ExternalLink,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useClubInfo } from '@/context/ClubInfoContext';
import { useNavCollapse } from '@/hooks/useNavCollapse';
import logoImage from '@/assets/logo.webp';

const LOGO_URL = logoImage;

export const Navigation = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [openMenu, setOpenMenu] = useState(null); // 'about' | 'sport' | null
  const location = useLocation();
  const aboutRef = useRef(null);
  const sportRef = useRef(null);
  const { clubInfo } = useClubInfo();

  // Selbstmessende Umschaltung Desktop <-> Hamburger: misst, ob Logo+Links+Buttons
  // tatsaechlich nebeneinander passen, und klappt sonst ins Hamburger-Menue
  // (ersetzt den festen lg-Breakpoint -> kann baulich nicht mehr ueberlappen).
  const { collapsed, rowRef, logoRef, linksRef, actionsRef, remeasure } = useNavCollapse({
    frozen: isOpen,
  });

  // clubInfo (z.B. easyVereinUrl -> "Mein Bereich"-Button, foundingYear -> Wortmarke)
  // laedt asynchron nach und veraendert die Breite der gemessenen Bloecke. Deterministisch
  // (RO-unabhaengig) vor dem naechsten Paint neu messen, damit es nie kurz ueberlappt.
  useLayoutEffect(() => {
    remeasure();
  }, [clubInfo.easyVereinUrl, clubInfo.foundingYear, remeasure]);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsOpen(false);
    setOpenMenu(null);
  }, [location]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      const insideAbout = aboutRef.current && aboutRef.current.contains(event.target);
      const insideSport = sportRef.current && sportRef.current.contains(event.target);
      if (!insideAbout && !insideSport) {
        setOpenMenu(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Mobile-Menue: Body-Scroll sperren + ESC schliesst, solange offen
  useEffect(() => {
    if (!isOpen) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e) => {
      if (e.key === 'Escape') setIsOpen(false);
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener('keydown', onKey);
    };
  }, [isOpen]);

  // Wenn die Leiste vom Hamburger zurueck in den Desktop-Modus wechselt (z.B.
  // Fenster verbreitern), offenes Mobil-Menue + Dropdowns schliessen, damit kein
  // verwaistes Panel im Desktop-Layout haengt und der Body-Scroll-Lock faellt.
  useEffect(() => {
    if (!collapsed) {
      setIsOpen(false);
      setOpenMenu(null);
    }
  }, [collapsed]);

  const navLinks = [{ path: '/', label: 'Startseite', icon: Home }];

  const aboutLinks = [
    { path: '/ueber-uns', label: 'Vereinsgeschichte', icon: Users },
    { path: '/einrichtungen', label: 'Anlagen & Einrichtungen', icon: Building },
    { path: '/vorstand', label: 'Vorstand', icon: UserCheck },
  ];

  const sportLinks = [
    { path: '/reitunterricht', label: 'Reitunterricht', icon: GraduationCap },
    { path: '/turniere', label: 'Turniere', icon: Award },
  ];

  const otherLinks = [
    { path: '/aktivitaeten', label: 'Aktivitäten', icon: Calendar },
    { path: '/galerie', label: 'Galerie', icon: Image },
    { path: '/sponsoren', label: 'Sponsoren', icon: Award },
    { path: '/kontakt', label: 'Kontakt', icon: Phone },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <>
      <nav
        aria-label="Hauptnavigation"
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled ? 'bg-white shadow-lg' : 'bg-white'
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div ref={rowRef} className="flex items-center justify-between h-16 lg:h-20">
            {/* Logo & Name */}
            <Link ref={logoRef} to="/" className="flex items-center gap-3 group flex-shrink-0">
              <img
                src={LOGO_URL}
                alt={`${clubInfo.name} Logo`}
                className="h-12 w-12 lg:h-14 lg:w-14 object-contain transition-transform group-hover:scale-105"
                width="56"
                height="56"
                decoding="async"
              />
              {/* Mobile-Wortmarke: kompakt, 2 Zeilen, lesbare 12px
                  (BITV-Empfehlung fuer Senior-Zielgruppe). */}
              <div className="block md:hidden leading-tight">
                <p className="font-display font-semibold text-foreground text-xs whitespace-nowrap">
                  Reit- und Fahrverein
                </p>
                <p className="text-xs text-muted-foreground whitespace-nowrap">Holle-Wüsting e.V.</p>
              </div>
              {/* Desktop-Wortmarke: drei kompakte Zeilen, damit der Block
                  schmal bleibt und nicht ins Menue rueberlaeuft. */}
              <div className="hidden md:block leading-tight">
                <p className="font-display font-semibold text-foreground text-xs lg:text-sm whitespace-nowrap">
                  Reit- und Fahrverein
                </p>
                <p className="text-xs text-muted-foreground whitespace-nowrap">Holle-Wüsting e.V.</p>
                <p className="text-xs text-muted-foreground whitespace-nowrap">von {clubInfo.foundingYear}</p>
              </div>
            </Link>

            {/* Desktop Navigation - Sichtbarkeit gemessen (collapsed) statt fixem lg-Breakpoint */}
            <div ref={linksRef} className={`${collapsed ? 'hidden' : 'flex'} items-center gap-1`}>
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap ${
                    isActive(link.path) ? 'bg-primary/10 text-primary' : 'text-foreground hover:bg-muted'
                  }`}
                >
                  {link.label}
                </Link>
              ))}

              {/* About Dropdown */}
              <div className="relative" ref={aboutRef}>
                <button
                  onClick={() => setOpenMenu(openMenu === 'about' ? null : 'about')}
                  aria-expanded={openMenu === 'about'}
                  aria-haspopup="true"
                  className={`flex items-center gap-1 px-3 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap ${
                    aboutLinks.some((l) => isActive(l.path))
                      ? 'bg-primary/10 text-primary'
                      : 'text-foreground hover:bg-muted'
                  }`}
                >
                  Über uns
                  <ChevronDown
                    className={`h-4 w-4 transition-transform ${openMenu === 'about' ? 'rotate-180' : ''}`}
                    aria-hidden="true"
                  />
                </button>

                {openMenu === 'about' && (
                  <div className="absolute top-full left-0 mt-2 w-56 bg-card rounded-xl shadow-xl border border-border overflow-hidden animate-slideDown">
                    {aboutLinks.map((link) => (
                      <Link
                        key={link.path}
                        to={link.path}
                        className={`flex items-center gap-3 px-4 py-3 text-sm transition-colors ${
                          isActive(link.path) ? 'bg-primary/10 text-primary' : 'text-foreground hover:bg-muted'
                        }`}
                      >
                        <link.icon className="h-4 w-4" />
                        {link.label}
                      </Link>
                    ))}
                  </div>
                )}
              </div>

              {/* Reitsport Dropdown */}
              <div className="relative" ref={sportRef}>
                <button
                  onClick={() => setOpenMenu(openMenu === 'sport' ? null : 'sport')}
                  aria-expanded={openMenu === 'sport'}
                  aria-haspopup="true"
                  className={`flex items-center gap-1 px-3 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap ${
                    sportLinks.some((l) => isActive(l.path))
                      ? 'bg-primary/10 text-primary'
                      : 'text-foreground hover:bg-muted'
                  }`}
                  data-testid="nav-reitsport-trigger"
                >
                  Reitsport
                  <ChevronDown
                    className={`h-4 w-4 transition-transform ${openMenu === 'sport' ? 'rotate-180' : ''}`}
                    aria-hidden="true"
                  />
                </button>

                {openMenu === 'sport' && (
                  <div className="absolute top-full left-0 mt-2 w-56 bg-card rounded-xl shadow-xl border border-border overflow-hidden animate-slideDown z-30">
                    {sportLinks.map((link) => (
                      <Link
                        key={link.path}
                        to={link.path}
                        className={`flex items-center gap-3 px-4 py-3 text-sm transition-colors ${
                          isActive(link.path) ? 'bg-primary/10 text-primary' : 'text-foreground hover:bg-muted'
                        }`}
                        data-testid={`nav-reitsport-${link.path.replace('/', '')}`}
                      >
                        <link.icon className="h-4 w-4" />
                        {link.label}
                      </Link>
                    ))}
                  </div>
                )}
              </div>

              {otherLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap ${
                    isActive(link.path) ? 'bg-primary/10 text-primary' : 'text-foreground hover:bg-muted'
                  }`}
                >
                  {link.label}
                </Link>
              ))}
            </div>

            {/* Actions */}
            <div className="flex items-center gap-2 flex-shrink-0">
              {/* Desktop-Actions (gemessen via actionsRef): Mitglied werden + Mein Bereich.
                  Mobile-Icon + Hamburger zaehlen NICHT zur Desktop-Breite und stehen ausserhalb. */}
              <div ref={actionsRef} className="flex items-center gap-2">
                <Link to="/mitgliedschaft" className="hidden sm:block">
                  <Button className="bg-primary hover:bg-primary-dark text-primary-foreground rounded-lg px-4 lg:px-6">
                    Mitglied werden
                  </Button>
                </Link>

                {clubInfo.easyVereinUrl && (
                  /* Desktop: voller Button mit Text */
                  <a
                    href={clubInfo.easyVereinUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hidden sm:block"
                    data-testid="nav-easyverein-link"
                    aria-label="Mein Bereich (öffnet EasyVerein in neuem Tab)"
                  >
                    <Button
                      variant="outline"
                      className="border-primary text-primary hover:bg-primary hover:text-primary-foreground rounded-lg px-3 lg:px-4 gap-2"
                      title="Öffnet den Mitgliederbereich bei EasyVerein in neuem Tab"
                    >
                      <LogIn className="h-4 w-4" aria-hidden="true" />
                      Mein Bereich
                      <ExternalLink className="h-3 w-3 opacity-70" aria-hidden="true" />
                    </Button>
                  </a>
                )}
              </div>

              {clubInfo.easyVereinUrl && (
                /* Mobile: nur Icon-Button - sichtbar NUR im eingeklappten Zustand und <640px,
                   damit der Login auch dort direkt im Header bleibt (statt im Hamburger unten).
                   OHNE target=_blank: Browser-Back funktioniert; das ExternalLink-Badge zeigt extern. */
                <a
                  href={clubInfo.easyVereinUrl}
                  className={`${collapsed ? 'inline-flex sm:hidden' : 'hidden'} relative`}
                  data-testid="nav-easyverein-link-mobile-icon"
                  aria-label="Mein Bereich auf EasyVerein (externe Seite)"
                  title="Mein Bereich - EasyVerein (externe Seite)"
                >
                  <Button
                    variant="outline"
                    size="icon"
                    className="border-primary text-primary hover:bg-primary hover:text-primary-foreground rounded-full h-11 w-11"
                  >
                    <LogIn className="h-5 w-5" aria-hidden="true" />
                  </Button>
                  <span
                    aria-hidden="true"
                    className="absolute -top-0.5 -right-0.5 inline-flex items-center justify-center w-4 h-4 rounded-full bg-primary text-primary-foreground"
                  >
                    <ExternalLink className="h-2.5 w-2.5" />
                  </span>
                </a>
              )}

              {/* Hamburger: sichtbar nur im eingeklappten Zustand (gemessen) */}
              <Button
                variant="ghost"
                size="icon"
                className={`${collapsed ? 'inline-flex' : 'hidden'} rounded-full h-11 w-11`}
                onClick={() => setIsOpen(!isOpen)}
                aria-label={isOpen ? 'Menü schließen' : 'Menü öffnen'}
                aria-expanded={isOpen}
                aria-controls={isOpen && collapsed ? 'mobile-nav-panel' : undefined}
              >
                {isOpen ? (
                  <X className="h-6 w-6" aria-hidden="true" />
                ) : (
                  <Menu className="h-6 w-6" aria-hidden="true" />
                )}
              </Button>
            </div>
          </div>
        </div>

        {/* Mobile Menu - nur wenn eingeklappt (collapsed) UND geoeffnet */}
        {isOpen && collapsed && (
          <div
            id="mobile-nav-panel"
            className="bg-card border-t border-border animate-slideDown max-h-[calc(100dvh-4rem)] overflow-y-auto"
            onClick={(e) => {
              // Schliessen, wenn ein Link/Button im Menue getappt wurde
              // (auch wenn der Klick auf die selbe Route zeigt).
              if (e.target.closest('a, button')) setIsOpen(false);
            }}
          >
            <div className="px-4 py-4 space-y-1">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-colors ${
                    isActive(link.path) ? 'bg-primary/10 text-primary' : 'text-foreground hover:bg-muted'
                  }`}
                >
                  <link.icon className="h-5 w-5" />
                  {link.label}
                </Link>
              ))}

              {/* About Section */}
              <div className="py-2" role="group" aria-labelledby="mnav-about-heading">
                <p id="mnav-about-heading" className="px-4 text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">
                  Über uns
                </p>
                {aboutLinks.map((link) => (
                  <Link
                    key={link.path}
                    to={link.path}
                    className={`flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-colors ${
                      isActive(link.path) ? 'bg-primary/10 text-primary' : 'text-foreground hover:bg-muted'
                    }`}
                  >
                    <link.icon className="h-5 w-5" />
                    {link.label}
                  </Link>
                ))}
              </div>

              {/* Reitsport Section */}
              <div className="py-2" role="group" aria-labelledby="mnav-sport-heading">
                <p id="mnav-sport-heading" className="px-4 text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">
                  Reitsport
                </p>
                {sportLinks.map((link) => (
                  <Link
                    key={link.path}
                    to={link.path}
                    className={`flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-colors ${
                      isActive(link.path) ? 'bg-primary/10 text-primary' : 'text-foreground hover:bg-muted'
                    }`}
                  >
                    <link.icon className="h-5 w-5" />
                    {link.label}
                  </Link>
                ))}
              </div>

              {otherLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-colors ${
                    isActive(link.path) ? 'bg-primary/10 text-primary' : 'text-foreground hover:bg-muted'
                  }`}
                >
                  <link.icon className="h-5 w-5" />
                  {link.label}
                </Link>
              ))}

              <Link to="/mitgliedschaft" className="block sm:hidden">
                <Button className="w-full mt-4 bg-primary hover:bg-primary-dark text-primary-foreground">
                  Mitglied werden
                </Button>
              </Link>

              {clubInfo.easyVereinUrl && (
                <a
                  href={clubInfo.easyVereinUrl}
                  rel="noopener noreferrer"
                  className="block sm:hidden"
                  data-testid="nav-easyverein-link-mobile"
                >
                  <Button variant="outline" className="w-full mt-2 border-primary text-primary gap-2">
                    <LogIn className="h-4 w-4" aria-hidden="true" />
                    Mein Bereich
                    <ExternalLink className="h-3 w-3 opacity-70" aria-hidden="true" />
                  </Button>
                </a>
              )}
            </div>
          </div>
        )}
      </nav>
    </>
  );
};

export default Navigation;
